// React
import React, { useState, useRef, useEffect } from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faHistory } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { DropdownPanel, DropdownPosition, DropdownDirection, DropdownAlignment, useDropdownPanel } from "../dropdown-panel/dropdown-panel.tsx";
import { getDataTestId } from "../functions/data-test-ids";

// CSS
import "./inputs.css";
import "./search-input.css";

function RecentSearchItem(props) {
    const clickHandler = () => {
        if (typeof props.OnSelect === "function") {
            props.OnSelect(props.SearchText);
        }
    };

    const deleteHandler = (e) => {
        e.stopPropagation();
        if (typeof props.OnDelete === "function") {
            props.OnDelete(props.Id);
        }
    };

    return (
        <div
            className="eone-ui-recent-search-item"
            role="button"
            tabIndex={0}
            onClick={clickHandler}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clickHandler(); } }}
            data-testid={getDataTestId(props.DataTestId, "recent-search-item")}>
            <FontAwesomeIcon icon={faHistory} className="eone-ui-recent-search-item-icon" />
            <span className="eone-ui-recent-search-item-text">{props.SearchText}</span>
            <a
                className="eone-ui-recent-search-item-delete"
                role="button"
                tabIndex={0}
                aria-label="Remove"
                onClick={deleteHandler}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); deleteHandler(e); } }}
                title="Remove"
                data-testid={getDataTestId(props.DataTestId, "recent-search-item-delete")}>
                <FontAwesomeIcon icon={faTimes} />
            </a>
        </div>
    );
}

function RecentSearchesDropdown(props) {
    if ((props.Visible !== undefined && !props.Visible) || !props.RecentSearches || props.RecentSearches.length === 0) {
        return null;
    }

    return (
        <div className="eone-ui-recent-searches-dropdown">
            <div className="eone-ui-recent-searches-header">Recent searches</div>
            <div className="eone-ui-recent-searches-list">
                {props.RecentSearches.map((search, index) => (
                    <RecentSearchItem
                        key={search.Id || index}
                        Id={search.Id}
                        SearchText={search.SearchText}
                        OnSelect={props.OnSelect}
                        OnDelete={props.OnDelete}
                        DataTestId={props.DataTestId}
                    />
                ))}
            </div>
            <div className="eone-ui-recent-searches-footer">
                <a
                    className="eone-ui-recent-searches-clear"
                    role="button"
                    tabIndex={0}
                    onClick={props.OnClear}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); props.OnClear?.(); } }}
                    data-testid={getDataTestId(props.DataTestId, "clear-recent-searches")}>
                    Clear recent searches
                </a>
            </div>
        </div>
    );
}

function SearchInput(props) {
    const [focused, setFocused] = useState(false);
    const inputRef = useRef(null);
    const dropdownPanelRef = useRef(null);

    const {
        open,
        setOpen,
        close,
        triggerRef,
        style,
    } = useDropdownPanel(DropdownPosition.Below, DropdownDirection.Down, DropdownAlignment.AlignLeftAndRight, 4, 300);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            const outsideTrigger = triggerRef?.current && !triggerRef.current.contains(e.target);
            const outsidePanel = dropdownPanelRef.current && !dropdownPanelRef.current.contains(e.target);
            if (outsideTrigger && outsidePanel) {
                close();
            }
        };
        if (open) {
            window.addEventListener("mousedown", handleClickOutside);
        }
        return () => window.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const changeHandler = (e) => {
        if (typeof props.OnChange === "function") {
            props.OnChange(e.target.value);
        }
    };

    const blurHandler = () => {
        setFocused(false);
        // Save search on blur
        if (typeof props.OnSaveSearch === "function" && props.Value && props.Value.trim().length >= 2) {
            props.OnSaveSearch(props.Value);
        }
        // Don't close immediately to allow clicking on dropdown items
        setTimeout(() => {
            if (!dropdownPanelRef.current?.contains(document.activeElement)) {
                close();
            }
        }, 150);
    };

    const focusHandler = () => {
        setFocused(true);
        // Only show dropdown if there are recent searches and no current value
        if (props.RecentSearches && props.RecentSearches.length > 0 && !props.Value) {
            setOpen(true);
        }
    };

    const keyDownHandler = (e) => {
        if (e.key === "Enter") {
            close();
            // Save search on Enter
            if (typeof props.OnSaveSearch === "function" && props.Value) {
                props.OnSaveSearch(props.Value);
            }
        }
        if (e.key === "Escape") {
            close();
        }
    };

    const selectRecentSearch = (searchText) => {
        if (typeof props.OnChange === "function") {
            props.OnChange(searchText);
        }
        close();
        inputRef.current?.focus();
    };

    const clearRecentSearches = () => {
        if (typeof props.OnClearSearches === "function") {
            props.OnClearSearches();
        }
        close();
    };

    const clearSearch = () => {
        if (typeof props.OnChange === "function") {
            props.OnChange("");
        }
        inputRef.current?.focus();
    };

    const getContainerClass = () => {
        let className = "eone-ui-input-container";
        if (focused) {
            className += " eone-ui-input-container-focused";
        }
        return className;
    };

    const showDropdown = open && props.RecentSearches && props.RecentSearches.length > 0;

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    return (
        <div className={getContainerClass()} ref={triggerRef}>
            <div className="eone-ui-input-icon">
                <FontAwesomeIcon icon={faSearch} />
            </div>
            <input
                ref={inputRef}
                type="text"
                className="eone-ui-input-string"
                placeholder={props.Placeholder || "Search"}
                value={props.Value || ""}
                onChange={changeHandler}
                onFocus={focusHandler}
                onBlur={blurHandler}
                onKeyDown={keyDownHandler}
                data-testid={props.DataTestId}
            />
            {props.Value && (
                <a
                    className="eone-ui-search-input-clear"
                    role="button"
                    tabIndex={0}
                    aria-label="Clear search"
                    onClick={clearSearch}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clearSearch(); } }}
                    title="Clear search"
                    data-testid={getDataTestId(props.DataTestId, "clear")}>
                    <FontAwesomeIcon icon={faTimes} />
                </a>
            )}
            <DropdownPanel
                Style={style}
                Open={showDropdown}
                Close={close}
                DropdownPanelRef={dropdownPanelRef}>
                <RecentSearchesDropdown
                    Visible={showDropdown}
                    RecentSearches={props.RecentSearches}
                    OnSelect={selectRecentSearch}
                    OnDelete={props.OnDeleteSearch}
                    OnClear={clearRecentSearches}
                    DataTestId={props.DataTestId}
                />
            </DropdownPanel>
        </div>
    );
}

export { SearchInput };
